import React, { useState, useEffect } from "react";
import { useSoundLibrary, InstrumentName, getInstrumentLabel } from "../../soundLibrary";
import PianoKeyboard from "../../components/PianoKeyboard";
import PlaybackOptionsPanel, { PlaybackOptions } from "../../components/PlaybackOptionsPanel";
import { SEO_METADATA } from "../../config/seoMetadata";
import SEO from "../../components/SEO";
import styles from "../../styles/SoundLibraryDemo.module.css";

const DEMO_INSTRUMENTS: InstrumentName[] = [
    "piano",
    "e-piano",
    "saw-synth",
    "square-synth",
    "sine-synth",
    "cello",
    "harp",
    "viola",
];

export default function SoundLibraryDemo() {
    const { playChord, playInterval, isInstrumentLoaded, loadInstrument, error, clearError } = useSoundLibrary();

    const [playbackOptions, setPlaybackOptions] = useState<PlaybackOptions>({
        instrument: "piano",
        volume: 70,
        muted: false,
        playMode: "harmonic",
    });

    const isCurrentInstrumentLoaded = isInstrumentLoaded(playbackOptions.instrument);

    // Auto-load initial instrument on mount
    useEffect(() => {
        loadInstrument(playbackOptions.instrument);
    }, [playbackOptions.instrument, loadInstrument]);

    const handlePlayChord = async () => {
        try {
            const velocity = playbackOptions.muted ? 0 : playbackOptions.volume * 0.01;
            await playChord(playbackOptions.instrument, ["C4", "E4", "G4"], { duration: 2, velocity });
        } catch (err) {
            console.error("Failed to play chord:", err);
        }
    };

    const handlePlayInterval = async () => {
        try {
            const velocity = playbackOptions.muted ? 0 : playbackOptions.volume * 0.01;
            await playInterval(playbackOptions.instrument, "C4", "G4", {
                simultaneousPlayback: playbackOptions.playMode === "harmonic",
                intervalDelayMs: 800,
                velocity,
            });
        } catch (err) {
            console.error("Failed to play interval:", err);
        }
    }

    return (
        <>
            <SEO {...SEO_METADATA.DEMO_SOUND} />
            <div className={styles.demoWrapper}>
            <div className={styles.header}>
                <h1 className={styles.title}>Sound Library Demo</h1>
                <p className={styles.subtitle}>
                    Test the sound library with different instruments and explore playback options
                </p>
            </div>

            {error && (
                <div className={styles.errorMessage}>
                    <div>
                        <strong>Error:</strong> {error.message}
                    </div>
                    <button onClick={clearError} className={styles.clearButton}>
                        Clear
                    </button>
                </div>
            )}

            <div className={styles.topSectionsWrapper}>
                <div className={styles.instrumentSection}>
                    <h2 className={styles.sectionTitle}>Playback Options</h2>
                    <div className={styles.instrumentSelectorWrapper}>
                        <PlaybackOptionsPanel
                            onChange={setPlaybackOptions}
                            initialOptions={playbackOptions}
                            availableInstruments={DEMO_INSTRUMENTS}
                            forcePlayMode="harmonic"
                        />
                    </div>
                </div>

                <div className={`${styles.pianoSectionWrapper} ${!isCurrentInstrumentLoaded ? styles.loading : ''}`}>
                    <div className={styles.contentSection}>
                        <h3>Piano Keyboard - Play Individual Notes</h3>
                        <p>
                            Click on the keys to play notes with the selected instrument. The sound
                            library automatically synthesizes missing notes using pitch shifting.
                        </p>
                        {!isCurrentInstrumentLoaded && (
                            <div className={styles.loadingOverlay}>
                                <div className={styles.loadingSpinner}></div>
                                <span>Loading instrument...</span>
                            </div>
                        )}
                        {isCurrentInstrumentLoaded && (
                            <PianoKeyboard 
                                instrument={playbackOptions.instrument} 
                                volume={playbackOptions.volume}
                                muted={playbackOptions.muted}
                            />
                        )}
                    </div>
                </div>

                <div className={styles.combinationSection}>
                    <h3>Play Combinations</h3>
                    <p>Test chord and interval playback with the current instrument.</p>
                    {!isCurrentInstrumentLoaded && (
                        <div className={styles.loadingOverlay}>
                            <div className={styles.loadingSpinner}></div>
                            <span>Loading instrument...</span>
                        </div>
                    )}
                    {isCurrentInstrumentLoaded && (
                        <div className={styles.combinationButtonsGrid}>
                            <button onClick={handlePlayChord} className={styles.chordButton}>
                                Play C Major Chord
                            </button>

                            <button onClick={handlePlayInterval} className={styles.intervalButton}>
                                Play C-G Interval ({playbackOptions.playMode})
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {isCurrentInstrumentLoaded && (
                <div className={styles.successMessage}>
                    <strong>✓ {getInstrumentLabel(playbackOptions.instrument)} is loaded and ready!</strong>
                    <br />
                    Click the buttons above to test sound playback.
                </div>
            )}
        </div>
        </>
    );
}
